import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Loader2,
  Clock,
  XCircle,
  BarChart3,
  ChevronDown,
  Search,
  Filter,
  ArrowUpDown,
  Info,
  Flame,
  Activity
} from 'lucide-react';

const ModelFailuresPanel = ({ adminConfigService }) => {
  // Estados
  const [stats, setStats] = useState(null);
  const [failures, setFailures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Filtros
  const [hoursAgo, setHoursAgo] = useState(24);
  const [modelFilter, setModelFilter] = useState('');
  const [limit, setLimit] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');

  // Expandir detalhes
  const [expandedRow, setExpandedRow] = useState(null);

  // ============================================
  // CARREGAR DADOS
  // ============================================

  const loadData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    setError(null);

    try {
      const [statsRes, failuresRes] = await Promise.all([
        adminConfigService.getModelFailureStats(hoursAgo),
        adminConfigService.getModelFailures({
          model_name: modelFilter || undefined,
          hours_ago: hoursAgo,
          limit
        })
      ]);

      setStats(statsRes.data || {});
      setFailures(failuresRes.data || []);
    } catch (err) {
      console.error('Erro ao carregar logs de falha:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao carregar dados');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [adminConfigService, hoursAgo, modelFilter, limit]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // HELPERS
  // ============================================

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      const date = new Date(ts);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return ts;
    }
  };

  const getRelativeTime = (ts) => {
    if (!ts) return '';
    try {
      const now = new Date();
      const date = new Date(ts);
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMin < 1) return 'agora';
      if (diffMin < 60) return `${diffMin}min atrás`;
      if (diffHours < 24) return `${diffHours}h atrás`;
      return `${diffDays}d atrás`;
    } catch {
      return '';
    }
  };

  const getModelColor = (modelName) => {
    const name = (modelName || '').toLowerCase();
    if (name.includes('dall')) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: 'bg-emerald-500' };
    if (name.includes('flux')) return { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', bar: 'bg-sky-500' };
    if (name.includes('nano') || name.includes('banana')) return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', bar: 'bg-amber-500' };
    if (name.includes('sora')) return { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', bar: 'bg-violet-500' };
    if (name.includes('veo')) return { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', bar: 'bg-indigo-500' };
    if (name.includes('kontext')) return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', bar: 'bg-rose-500' };
    if (name.includes('placid')) return { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', bar: 'bg-teal-500' };
    return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', bar: 'bg-gray-500' };
  };

  const getSeverityIcon = (attempt) => {
    if (attempt >= 3) return <Flame className="w-4 h-4 text-red-500" />;
    if (attempt >= 2) return <AlertTriangle className="w-4 h-4 text-orange-500" />;
    return <XCircle className="w-4 h-4 text-yellow-500" />;
  };

  // Filtrar localmente por busca
  const filteredFailures = failures.filter(f => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      f.model_name?.toLowerCase().includes(term) ||
      f.error_message?.toLowerCase().includes(term) ||
      f.prompt_preview?.toLowerCase().includes(term)
    );
  });

  // Ordenar
  const sortedFailures = [...filteredFailures].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDir === 'desc' ? -cmp : cmp;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Modelos únicos para filtro
  const uniqueModels = [...new Set(failures.map(f => f.model_name).filter(Boolean))];

  // ============================================
  // LOADING
  // ============================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Carregando logs de falha...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================

  const maxFailures = stats?.summary_by_model
    ? Math.max(...Object.values(stats.summary_by_model), 1)
    : 1;

  return (
    <div className="space-y-6">

      {/* ===== CARDS DE ESTATÍSTICAS ===== */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total de falhas */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-red-50 rounded-xl">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total de Falhas</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_failures || 0}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              Últimas {hoursAgo}h
            </p>
          </div>

          {/* Modelo mais problemático */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-orange-50 rounded-xl">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Modelo com Mais Falhas</p>
                <p className="text-lg font-bold text-gray-900 truncate">
                  {stats.most_failing_model || '—'}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              {stats.most_failing_count || 0} falhas no período
            </p>
          </div>

          {/* Modelos afetados */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-amber-50 rounded-xl">
                <Activity className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Modelos Afetados</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.summary_by_model ? Object.keys(stats.summary_by_model).length : 0}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400">
              De todos os modelos configurados
            </p>
          </div>
        </div>
      )}

      {/* ===== GRÁFICO DE BARRAS POR MODELO ===== */}
      {stats?.summary_by_model && Object.keys(stats.summary_by_model).length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            Falhas por Modelo
          </h3>

          <div className="space-y-3">
            {Object.entries(stats.summary_by_model)
              .sort((a, b) => b[1] - a[1])
              .map(([model, count]) => {
                const colors = getModelColor(model);
                const pct = Math.round((count / maxFailures) * 100);
                return (
                  <div key={model} className="flex items-center gap-3">
                    <span className={`text-sm font-medium w-40 truncate ${colors.text}`}>
                      {model}
                    </span>
                    <div className="flex-1 h-7 bg-gray-100 rounded-lg overflow-hidden relative">
                      <div
                        className={`h-full ${colors.bar} rounded-lg transition-all duration-500`}
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-700">
                        {count}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* ===== FILTROS E AÇÕES ===== */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-3 items-center">
          {/* Busca */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por modelo, erro ou prompt..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Filtro por modelo */}
          <select
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent cursor-pointer"
          >
            <option value="">Todos os modelos</option>
            {uniqueModels.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          {/* Período */}
          <select
            value={hoursAgo}
            onChange={(e) => setHoursAgo(parseInt(e.target.value))}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent cursor-pointer"
          >
            <option value={1}>Última 1h</option>
            <option value={6}>Últimas 6h</option>
            <option value={12}>Últimas 12h</option>
            <option value={24}>Últimas 24h</option>
            <option value={72}>Últimas 72h</option>
            <option value={168}>Última semana</option>
            <option value={720}>Último mês</option>
          </select>

          {/* Limite */}
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:ring-2 focus:ring-amber-500 focus:border-transparent cursor-pointer"
          >
            <option value={50}>50 registros</option>
            <option value={100}>100 registros</option>
            <option value={200}>200 registros</option>
            <option value={500}>500 registros</option>
          </select>

          {/* Refresh */}
          <button
            onClick={() => loadData(true)}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* ===== ERRO ===== */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* ===== TABELA DE FALHAS ===== */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Log de Falhas
          </h3>
          <span className="text-sm text-gray-500">
            {sortedFailures.length} {sortedFailures.length === 1 ? 'registro' : 'registros'}
          </span>
        </div>

        {sortedFailures.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-50 rounded-full mb-4">
              <Activity className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-gray-600 font-medium text-lg mb-1">Nenhuma falha encontrada</p>
            <p className="text-gray-400 text-sm">
              {modelFilter
                ? `Nenhuma falha do modelo "${modelFilter}" no período.`
                : `Nenhuma falha nas últimas ${hoursAgo}h. Tudo operando normalmente! 🎉`
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 w-10">#</th>
                  <th
                    className="px-4 py-3 text-left font-semibold text-gray-600 cursor-pointer hover:text-amber-600 select-none"
                    onClick={() => toggleSort('timestamp')}
                  >
                    <span className="flex items-center gap-1">
                      Data/Hora
                      <ArrowUpDown className="w-3 h-3" />
                    </span>
                  </th>
                  <th
                    className="px-4 py-3 text-left font-semibold text-gray-600 cursor-pointer hover:text-amber-600 select-none"
                    onClick={() => toggleSort('model_name')}
                  >
                    <span className="flex items-center gap-1">
                      Modelo
                      <ArrowUpDown className="w-3 h-3" />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">
                    Mensagem de Erro
                  </th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600 w-20">
                    Tentativa
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 w-24">
                    Tempo
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sortedFailures.map((failure, idx) => {
                  const colors = getModelColor(failure.model_name);
                  const isExpanded = expandedRow === failure.id;

                  return (
                    <React.Fragment key={failure.id || idx}>
                      <tr
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-amber-50/50' : ''}`}
                        onClick={() => setExpandedRow(isExpanded ? null : failure.id)}
                      >
                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {formatTimestamp(failure.timestamp)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border}`}>
                            {failure.model_name || 'Desconhecido'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs">
                          <p className="truncate" title={failure.error_message}>
                            {failure.error_message || '—'}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {failure.fallback_attempt != null ? (
                            <span className="inline-flex items-center gap-1">
                              {getSeverityIcon(failure.fallback_attempt)}
                              <span className="text-xs font-bold text-gray-700">
                                #{failure.fallback_attempt}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                          {getRelativeTime(failure.timestamp)}
                        </td>
                      </tr>

                      {/* Linha expandida com detalhes */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="px-4 py-4 bg-amber-50/30">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                              {/* Erro completo */}
                              {failure.error_message && (
                                <div className="md:col-span-2">
                                  <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Erro Completo</p>
                                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                                    <code className="text-xs text-red-700 whitespace-pre-wrap break-all">
                                      {failure.error_message}
                                    </code>
                                  </div>
                                </div>
                              )}

                              {/* Preview do prompt */}
                              {failure.prompt_preview && (
                                <div className="md:col-span-2">
                                  <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Prompt (Preview)</p>
                                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                                    <p className="text-xs text-gray-700 italic">
                                      "{failure.prompt_preview}"
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Metadados */}
                              <div>
                                <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Detalhes</p>
                                <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-1.5">
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">ID do Log:</span>
                                    <span className="font-mono text-gray-700">{failure.id}</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Tipo:</span>
                                    <span className="text-gray-700">{failure.type}</span>
                                  </div>
                                  {failure.fallback_attempt != null && (
                                    <div className="flex justify-between text-xs">
                                      <span className="text-gray-500">Tentativa no Fallback:</span>
                                      <span className="font-bold text-gray-700">#{failure.fallback_attempt}</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Extra data */}
                              {failure.extra_data && Object.keys(failure.extra_data).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Dados Extras</p>
                                  <div className="bg-white border border-gray-200 rounded-xl p-3">
                                    <pre className="text-xs text-gray-600 whitespace-pre-wrap break-all">
                                      {JSON.stringify(failure.extra_data, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== INFO ===== */}
      <div className="flex items-center gap-2 text-xs text-gray-400 px-2">
        <Info className="w-3.5 h-3.5" />
        <span>
          Logs registrados automaticamente quando um modelo de geração falha. 
          Clique em uma linha para expandir detalhes.
        </span>
      </div>
    </div>
  );
};

export default ModelFailuresPanel;